"use client";

import Link from "next/link";
import { FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Button, Input, Panel } from "@/components/ui";
import { useToast } from "@/components/toast";
import { api } from "@/lib/api";
import { startPageProgress } from "@/lib/progress";
import { useI18n } from "@/components/shell";

export default function LoginPage() {
  const router = useRouter();
  const { tt } = useI18n();
  const { toastError } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("idle") === "1") toastError(tt("idleLoggedOut"));
    if (params.get("kicked") === "1") toastError(tt("sessionKicked"));
  }, [tt, toastError]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const me = await api<{ role: string }>("/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      startPageProgress();
      router.push(me.role === "admin" ? "/admin/servers" : "/dashboard");
    } catch (err) {
      toastError(err instanceof Error ? err.message : tt("loginFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md">
      <Panel className="rounded-lg border-slate-700 bg-slate-800 p-8 shadow-2xl">
        <h1 className="text-2xl font-bold text-white" suppressHydrationWarning>
          {tt("loginTitle")}
        </h1>
        <p className="mt-2 text-sm text-slate-400" suppressHydrationWarning>
          {tt("loginHint")}
        </p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Input label={tt("email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input label={tt("password")} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <p className="text-right text-xs">
            <Link href="/forgot" className="link-accent">
              {tt("forgotPassword")}
            </Link>
          </p>
          <Button className="w-full" disabled={busy}>
            {busy ? tt("connecting") : tt("login")}
          </Button>
        </form>
        <p className="mt-4 text-center text-xs text-slate-500">
          {tt("noAccount")}{" "}
          <Link href="/register" className="link-accent">
            {tt("register")}
          </Link>
        </p>
      </Panel>
    </div>
  );
}
